/**
 * The Vitrine emoticon library: 126 illustrated stickers in 8 themed groups.
 *
 * Art ships as WebP under web/public/emoji/<code>.webp (referenced by URL, NOT
 * imported) so none of it enters the JS bundle — the browser fetches only the
 * emoji actually rendered. Codes MUST match backend/config/discussionEmoji.js,
 * which validates reactions and inline tokens; the pair is locked by
 * emojiCatalogParity.test.js.
 *
 * Group order is picker order: the universal reaction faces come first.
 */

// Display name of the curated library (shown in the picker).
export const PACK_NAME = "Vitrine Emoticons";

/** Public URL for a built-in emoji's artwork. */
export const emojiUrl = (code) => `${process.env.PUBLIC_URL || ""}/emoji/${code}.webp`;

/**
 * Groups render as picker sections. `name` is the English label; the picker
 * translates via the `emoji.groups.<key>` i18n keys and falls back to this.
 */
export const EMOJI_GROUPS = [
  { key: "faces", name: "Reactions" },
  { key: "school", name: "School" },
  { key: "class", name: "Classroom Chaos" },
  { key: "math", name: "Maths" },
  { key: "cosmic", name: "Cosmic" },
  { key: "sport", name: "Sports" },
  { key: "cat", name: "Big Cats" },
  { key: "ride", name: "Getting Around" },
];

export const DISCUSSION_EMOJI = [
  // Reactions
  { code: "face-anger", name: "Anger", group: "faces", keywords: ["angry", "mad", "rage", "furious", "grr"] },
  { code: "face-boredom", name: "Boredom", group: "faces", keywords: ["bored", "meh", "dull", "yawn", "whatever"] },
  { code: "face-confusion", name: "Confusion", group: "faces", keywords: ["confused", "huh", "what", "lost", "unsure", "idk"] },
  { code: "face-disgust", name: "Disgust", group: "faces", keywords: ["gross", "ew", "yuck", "nope"] },
  { code: "face-embarrassment", name: "Embarrassment", group: "faces", keywords: ["blush", "oops", "awkward", "shy", "cringe"] },
  { code: "face-fear", name: "Fear", group: "faces", keywords: ["scared", "afraid", "yikes", "worried", "eek"] },
  { code: "face-joy", name: "Joy", group: "faces", keywords: ["happy", "laugh", "haha", "lol", "grin", "yay"] },
  { code: "face-love", name: "Love", group: "faces", keywords: ["heart", "adore", "crush", "like", "hearts"] },
  { code: "face-mischief", name: "Mischief", group: "faces", keywords: ["cheeky", "sneaky", "playful", "prank", "evil grin"] },
  { code: "face-relief", name: "Relief", group: "faces", keywords: ["phew", "relieved", "finally", "made it"] },
  { code: "face-sadness", name: "Sadness", group: "faces", keywords: ["sad", "cry", "tears", "unhappy", "sorry"] },
  { code: "face-sleepy", name: "Sleepy", group: "faces", keywords: ["tired", "sleep", "yawn", "exhausted", "zzz"] },
  { code: "face-smug", name: "Smug", group: "faces", keywords: ["smirk", "proud", "told you", "pleased"] },
  { code: "face-surprise", name: "Surprise", group: "faces", keywords: ["wow", "shocked", "omg", "whoa", "amazed"] },
  // School
  { code: "school-backpack", name: "Backpack", group: "school", keywords: ["bag", "pack", "school"] },
  { code: "school-book", name: "Book", group: "school", keywords: ["read", "study", "reading"] },
  { code: "school-chasing-students", name: "Chasing Students", group: "school", keywords: ["chase", "late", "run"] },
  { code: "school-computer-kisses", name: "Computer Kisses", group: "school", keywords: ["love", "online", "affection"] },
  { code: "school-heart", name: "Heart", group: "school", keywords: ["love", "like", "favourite"] },
  { code: "school-laptop", name: "Laptop", group: "school", keywords: ["computer", "online", "typing"] },
  { code: "school-love-it", name: "Love It", group: "school", keywords: ["love", "great", "awesome"] },
  { code: "school-pencil", name: "Pencil", group: "school", keywords: ["write", "notes", "writing"] },
  { code: "school-running-student", name: "Running Late", group: "school", keywords: ["late", "rush", "hurry", "deadline"] },
  { code: "school-thank-you", name: "Thank You", group: "school", keywords: ["thanks", "grateful", "ty", "appreciate"] },
  // Classroom Chaos
  { code: "class-backpack-spill", name: "Backpack Spill", group: "class", keywords: ["mess", "chaos", "spill", "disaster"] },
  { code: "class-calculator-confused", name: "Calculator Confused", group: "class", keywords: ["confused", "maths", "lost", "huh"] },
  { code: "class-crying-paper", name: "Crying Paper", group: "class", keywords: ["sad", "cry", "failed", "bad grade"] },
  { code: "class-eraser-panic", name: "Eraser Panic", group: "class", keywords: ["panic", "mistake", "undo", "stress"] },
  { code: "class-glue-stuck", name: "Glue Stuck", group: "class", keywords: ["stuck", "blocked", "help"] },
  { code: "class-heart-shy", name: "Heart Shy", group: "class", keywords: ["shy", "love", "blush", "crush"] },
  { code: "class-homework-monster", name: "Homework Monster", group: "class", keywords: ["homework", "overwhelmed", "too much", "assignment"] },
  { code: "class-late-student", name: "Late Student", group: "class", keywords: ["late", "deadline", "rush", "overdue"] },
  { code: "class-laughing", name: "Laughing", group: "class", keywords: ["lol", "haha", "funny", "laugh"] },
  { code: "class-oops", name: "Oops", group: "class", keywords: ["mistake", "sorry", "my bad", "error"] },
  { code: "class-pencil-tantrum", name: "Pencil Tantrum", group: "class", keywords: ["angry", "frustrated", "rage"] },
  { code: "class-ruler-dance", name: "Ruler Dance", group: "class", keywords: ["dance", "celebrate", "happy"] },
  { code: "class-scissors-scared", name: "Scissors Scared", group: "class", keywords: ["scared", "nervous", "cut"] },
  { code: "class-sleepy-book", name: "Sleepy Book", group: "class", keywords: ["tired", "boring", "sleep", "study"] },
  { code: "class-sneezy-laptop", name: "Sneezy Laptop", group: "class", keywords: ["sick", "sneeze", "broken", "virus"] },
  // Maths
  { code: "math-angle", name: "Angle", group: "math", keywords: ["degrees", "geometry", "corner"] },
  { code: "math-approx-less", name: "Approximately Less Than", group: "math", keywords: ["about", "roughly", "at most"] },
  { code: "math-approximately", name: "Approximately", group: "math", keywords: ["about", "roughly", "circa", "approx"] },
  { code: "math-brackets", name: "Brackets", group: "math", keywords: ["braces", "grouping"] },
  { code: "math-decimal", name: "Decimal", group: "math", keywords: ["point", "place value"] },
  { code: "math-derivative", name: "Derivative", group: "math", keywords: ["calculus", "rate of change", "slope"] },
  { code: "math-divide", name: "Divide", group: "math", keywords: ["division", "split"] },
  { code: "math-equals", name: "Equals", group: "math", keywords: ["equal", "same", "answer", "result"] },
  { code: "math-fraction", name: "Fraction", group: "math", keywords: ["ratio", "part", "half"] },
  { code: "math-greater", name: "Greater", group: "math", keywords: ["bigger", "more than", "larger"] },
  { code: "math-infinity", name: "Infinity", group: "math", keywords: ["endless", "forever", "unlimited"] },
  { code: "math-integral", name: "Integral", group: "math", keywords: ["calculus", "area under"] },
  { code: "math-less", name: "Less", group: "math", keywords: ["smaller", "fewer", "under"] },
  { code: "math-minus", name: "Minus", group: "math", keywords: ["subtract", "take away", "negative"] },
  { code: "math-multiply", name: "Multiply", group: "math", keywords: ["times", "product"] },
  { code: "math-not", name: "Not", group: "math", keywords: ["negation", "logic"] },
  { code: "math-not-equal", name: "Not Equal", group: "math", keywords: ["different", "unequal"] },
  { code: "math-parallel", name: "Parallel", group: "math", keywords: ["lines", "geometry"] },
  { code: "math-parentheses", name: "Parentheses", group: "math", keywords: ["brackets", "grouping"] },
  { code: "math-percent", name: "Percent", group: "math", keywords: ["percentage", "score", "grade"] },
  { code: "math-pi", name: "Pi", group: "math", keywords: ["circle", "3.14", "constant"] },
  { code: "math-plus", name: "Plus", group: "math", keywords: ["add", "addition", "sum"] },
  { code: "math-power", name: "To The Power Of", group: "math", keywords: ["exponent", "squared", "index"] },
  { code: "math-square-root", name: "Square Root", group: "math", keywords: ["root", "radical", "surd"] },
  { code: "math-sum", name: "Sum", group: "math", keywords: ["sigma", "total", "add up", "series"] },
  // Cosmic
  { code: "cosmic-aurora", name: "Aurora", group: "cosmic", keywords: ["lights", "northern lights", "sky"] },
  { code: "cosmic-black-hole", name: "Black Hole", group: "cosmic", keywords: ["void", "gone", "vanished", "space"] },
  { code: "cosmic-comet", name: "Comet", group: "cosmic", keywords: ["fast", "speed", "shooting star"] },
  { code: "cosmic-debris", name: "Debris", group: "cosmic", keywords: ["asteroid", "rocks", "space junk"] },
  { code: "cosmic-galaxy", name: "Galaxy", group: "cosmic", keywords: ["space", "stars", "universe"] },
  { code: "cosmic-lightning", name: "Lightning", group: "cosmic", keywords: ["fast", "energy", "power", "storm", "zap"] },
  { code: "cosmic-moon", name: "Moon", group: "cosmic", keywords: ["night", "sleep", "late"] },
  { code: "cosmic-planet", name: "Planet", group: "cosmic", keywords: ["space", "world", "orbit"] },
  { code: "cosmic-rainbow", name: "Rainbow", group: "cosmic", keywords: ["colour", "pride", "hope", "bright"] },
  { code: "cosmic-river", name: "River", group: "cosmic", keywords: ["water", "flow", "stream"] },
  { code: "cosmic-star", name: "Star", group: "cosmic", keywords: ["gold star", "great", "well done", "top", "award"] },
  { code: "cosmic-storm", name: "Storm", group: "cosmic", keywords: ["weather", "trouble", "chaos"] },
  { code: "cosmic-stream", name: "Stream", group: "cosmic", keywords: ["water", "flow", "current"] },
  { code: "cosmic-sun", name: "Sun", group: "cosmic", keywords: ["sunny", "bright", "day", "shine"] },
  { code: "cosmic-wind", name: "Wind", group: "cosmic", keywords: ["breeze", "blow", "air"] },
  // Sports
  { code: "sport-baseball-surprise", name: "Baseball Surprise", group: "sport", keywords: ["baseball", "surprise"] },
  { code: "sport-basketball-dunk", name: "Basketball Dunk", group: "sport", keywords: ["basketball", "dunk", "score"] },
  { code: "sport-boxer-hug", name: "Boxer Hug", group: "sport", keywords: ["boxing", "friendly", "truce", "peace"] },
  { code: "sport-cyclist-backwards", name: "Cyclist Backwards", group: "sport", keywords: ["cycling", "bike", "wrong way", "backwards"] },
  { code: "sport-fan-facepaint", name: "Fan Facepaint", group: "sport", keywords: ["fan", "supporter", "cheer", "team spirit"] },
  { code: "sport-golfer-cup", name: "Golfer Cup", group: "sport", keywords: ["golf", "hole in one"] },
  { code: "sport-gymnast-stuck", name: "Gymnast Stuck", group: "sport", keywords: ["gymnastics", "stuck", "flexible"] },
  { code: "sport-referee-confused", name: "Referee Confused", group: "sport", keywords: ["referee", "rules", "unfair", "confused"] },
  { code: "sport-relay-pileup", name: "Relay Pileup", group: "sport", keywords: ["running", "race", "team", "crash", "chaos"] },
  { code: "sport-skateboard-flip", name: "Skateboard Flip", group: "sport", keywords: ["skate", "trick", "flip"] },
  { code: "sport-soccer-miss", name: "Soccer Miss", group: "sport", keywords: ["football", "miss", "fail", "so close"] },
  { code: "sport-swimmer-floaties", name: "Swimmer Floaties", group: "sport", keywords: ["swim", "beginner", "learning"] },
  { code: "sport-tennis-tangle", name: "Tennis Tangle", group: "sport", keywords: ["tennis", "tangled", "mess"] },
  { code: "sport-trophy-dance", name: "Trophy Dance", group: "sport", keywords: ["win", "winner", "champion", "victory", "celebrate", "first"] },
  // Big Cats
  { code: "cat-cheetah", name: "Cheetah", group: "cat", keywords: ["fast", "speed", "quick"] },
  { code: "cat-cougar", name: "Cougar", group: "cat", keywords: ["puma", "mountain lion"] },
  { code: "cat-jaguar", name: "Jaguar", group: "cat", keywords: ["wild", "jungle"] },
  { code: "cat-leopard", name: "Leopard", group: "cat", keywords: ["spots", "wild"] },
  { code: "cat-lion", name: "Lion", group: "cat", keywords: ["mascot", "brave", "king", "roar", "pride"] },
  { code: "cat-lynx", name: "Lynx", group: "cat", keywords: ["wild", "cat"] },
  { code: "cat-snow-leopard", name: "Snow Leopard", group: "cat", keywords: ["snow", "mountain", "rare"] },
  { code: "cat-tiger", name: "Tiger", group: "cat", keywords: ["mascot", "fierce", "stripes"] },
  // Getting Around
  { code: "ride-airplane", name: "Airplane", group: "ride", keywords: ["flight", "fly", "travel", "trip"] },
  { code: "ride-bicycle", name: "Bicycle", group: "ride", keywords: ["bike", "cycle", "ride"] },
  { code: "ride-boat", name: "Boat", group: "ride", keywords: ["sail", "sea", "water"] },
  { code: "ride-camel-ride", name: "Camel Ride", group: "ride", keywords: ["camel", "desert", "travel"] },
  { code: "ride-canoe", name: "Canoe", group: "ride", keywords: ["paddle", "river", "water"] },
  { code: "ride-car", name: "Car", group: "ride", keywords: ["drive", "travel"] },
  { code: "ride-cloud-bus", name: "Cloud Bus", group: "ride", keywords: ["bus", "fly", "dream"] },
  { code: "ride-crocodile-ride", name: "Crocodile Ride", group: "ride", keywords: ["crocodile", "risky", "wild"] },
  { code: "ride-elephant-ride", name: "Elephant Ride", group: "ride", keywords: ["elephant", "safari", "ride"] },
  { code: "ride-flying-broom", name: "Flying Broom", group: "ride", keywords: ["magic", "witch", "fly"] },
  { code: "ride-flying-car", name: "Flying Car", group: "ride", keywords: ["future", "fly", "drive"] },
  { code: "ride-horse-ride", name: "Horse Ride", group: "ride", keywords: ["horse", "ride", "gallop"] },
  { code: "ride-hot-air-balloon", name: "Hot Air Balloon", group: "ride", keywords: ["balloon", "fly", "float"] },
  { code: "ride-kayak", name: "Kayak", group: "ride", keywords: ["paddle", "river", "water"] },
  { code: "ride-monorail", name: "Monorail", group: "ride", keywords: ["train", "rail", "future"] },
  { code: "ride-motorbike", name: "Motorbike", group: "ride", keywords: ["bike", "motorcycle", "ride"] },
  { code: "ride-racecar", name: "Racecar", group: "ride", keywords: ["fast", "race", "speed"] },
  { code: "ride-rocket-skate", name: "Rocket Skates", group: "ride", keywords: ["fast", "speed", "launch", "go", "zoom"] },
  { code: "ride-roller-skates", name: "Roller Skates", group: "ride", keywords: ["skate", "roll"] },
  { code: "ride-scooter", name: "Scooter", group: "ride", keywords: ["ride", "commute"] },
  { code: "ride-spaceship", name: "Spaceship", group: "ride", keywords: ["rocket", "space", "launch", "blast off"] },
  { code: "ride-supercar", name: "Supercar", group: "ride", keywords: ["fast", "drive", "luxury"] },
  { code: "ride-surfboard", name: "Surfboard", group: "ride", keywords: ["surf", "wave", "beach"] },
  { code: "ride-train", name: "Train", group: "ride", keywords: ["rail", "commute", "travel"] },
  { code: "ride-whale-ride", name: "Whale Ride", group: "ride", keywords: ["whale", "sea", "ocean"] },
];

export const EMOJI_BY_CODE = Object.fromEntries(DISCUSSION_EMOJI.map((e) => [e.code, e]));
export const EMOJI_CODES = DISCUSSION_EMOJI.map((e) => e.code);
const BUILTIN_CODE_SET = new Set(EMOJI_CODES);

/**
 * Free-text match over name, code, and keywords. Used by the picker's search;
 * with 126 emoji a flat scroll is no longer navigable on its own.
 */
export const searchEmoji = (list, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (e) =>
      e.name.toLowerCase().includes(q) ||
      e.code.includes(q) ||
      (e.keywords || []).some((k) => k.includes(q))
  );
};

// Matches :code: tokens; codes are lowercase + hyphens only. Rebuild a fresh
// RegExp when scanning (this has the /g flag and thus stateful lastIndex).
export const EMOJI_TOKEN = /:([a-z-]+):/g;

/**
 * Splits a raw string into ordered segments: { type: "text", value } and
 * { type: "emoji", code }. Only KNOWN codes become emoji; unknown :tokens: stay
 * as literal text. Pure (no React) so it's unit-testable and never touches HTML.
 */
export const parseEmoji = (text, knownCodes) => {
  const known = knownCodes || BUILTIN_CODE_SET; // default: built-in library only
  const segments = [];
  if (!text) return segments;
  const re = new RegExp(EMOJI_TOKEN.source, "g");
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const [token, code] = m;
    if (!known.has(code)) continue; // unknown -> leave inside the next text run
    if (m.index > last) segments.push({ type: "text", value: text.slice(last, m.index) });
    segments.push({ type: "emoji", code });
    last = m.index + token.length;
  }
  if (last < text.length) segments.push({ type: "text", value: text.slice(last) });
  return segments;
};
